import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';

import { Clock, DsLoggingService, ShutdownReason } from '@cubigo/digital-signage';

import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class ServiceWorkerUpdaterService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly logging = inject(DsLoggingService);
  private readonly updates = inject(SwUpdate);
  private readonly clock = inject(Clock);
  private initialized = false;
  private checking = false;

  public init(): void {
    if (this.initialized || !this.updates.isEnabled) {
      return;
    }

    this.initialized = true;
    this.watchUpdates();
    const updateInterval = environment.name === 'prod' || environment.name === 'uat' ? 30 * 60 * 1000 : 10 * 60 * 1000;
    const polling = this.clock.every(updateInterval, 0, () => this.checkForUpdate());
    this.destroyRef.onDestroy(() => polling.dispose());
  }

  private checkForUpdate(): void {
    if (this.checking) {
      return;
    }

    this.checking = true;
    void this.updates.checkForUpdate().catch(() => {
      this.logging.trackEvent('service_worker_check_failed', { reason: 'check-failed' });
    }).finally(() => { this.checking = false; });
  }

  private watchUpdates(): void {
    this.updates.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      this.logging.trackEvent('service_worker_version', { versionEvent: event.type });

      if (event.type === 'VERSION_READY') {
        this.logging.markCleanShutdown(ShutdownReason.SwUpdate);
        this.reload();
      }
    });
    this.updates.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.logging.trackEvent('service_worker_unrecoverable', { reason: 'unrecoverable' });
    });
  }

  private reload(): void {
    window.location.reload();
  }
}
